/**
 * Review popup — the card's reviews panel. Hover on a pointer device, tap on
 * touch, as live does.
 *
 * Live opens it from the whole rating row (`.ProductItem__Rating`), servings
 * label included. A row that carries `data-review-popup-host` is the trigger
 * area here too; without it the popup element itself is, as before.
 *
 * Okendo only scans on page load, so a popup arriving in a fragment swap keeps
 * an empty panel. Asked for here rather than from a section so every consumer
 * of the card is covered; debounced because a swapped grid connects two dozen.
 */
let okendoScanTimer;

const scheduleOkendoScan = () => {
  clearTimeout(okendoScanTimer);
  okendoScanTimer = setTimeout(() => window.okeWidgetApi?.initAllWidgets(), 100);
};

if (!customElements.get('review-popup')) {
  customElements.define(
    'review-popup',
    class ReviewPopup extends HTMLElement {
      closeTimer = undefined;

      get trigger() {
        return this.querySelector('.review-popup__rating');
      }

      get panel() {
        return this.querySelector('.review-popup__panel');
      }

      get host() {
        return this.closest('[data-review-popup-host]') ?? this;
      }

      get isPointer() {
        return window.matchMedia('(hover: hover)').matches;
      }

      connectedCallback() {
        scheduleOkendoScan();
        this.hostEl = this.host;
        this.addEventListener('click', this.onClick);
        this.hostEl.addEventListener('mouseenter', this.onEnter);
        this.hostEl.addEventListener('mouseleave', this.onLeave);
        if (this.hostEl !== this) this.hostEl.addEventListener('click', this.onHostClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
        // Capture on document: `scroll` does not bubble and the scroller is not
        // always the window — window alone fired nothing.
        document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      }

      disconnectedCallback() {
        clearTimeout(this.closeTimer);
        this.hostEl?.removeEventListener('mouseenter', this.onEnter);
        this.hostEl?.removeEventListener('mouseleave', this.onLeave);
        this.hostEl?.removeEventListener('click', this.onHostClick);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('scroll', this.onScroll, { capture: true });
      }

      onEnter = () => {
        if (!this.isPointer) return;
        clearTimeout(this.closeTimer);
        this.open();
      };

      // Grace period, so crossing the gap to the panel does not shut it.
      onLeave = () => {
        if (!this.isPointer) return;
        this.closeTimer = setTimeout(() => this.close(), 150);
      };

      onClick = (event) => {
        if (event.target.closest('[data-review-popup-close]')) {
          this.close();
          this.trigger?.focus();
          return;
        }
        if (event.target.closest('.review-popup__rating')) {
          event.preventDefault();
          this.panel?.hidden ? this.open() : this.close();
        }
      };

      // The rest of the host row, the servings label, opens it too. Taps toggle.
      onHostClick = (event) => {
        if (this.contains(event.target)) return;
        if (this.isPointer) this.open();
        else this.panel?.hidden ? this.open() : this.close();
      };

      onKeydown = (event) => {
        if (event.key === 'Escape' && !this.panel?.hidden) {
          this.close();
          this.trigger?.focus();
          return;
        }
        // role="button" does not bring Enter and Space with it.
        if ((event.key === 'Enter' || event.key === ' ') && event.target.closest('.review-popup__rating')) {
          event.preventDefault();
          this.panel?.hidden ? this.open() : this.close();
        }
      };

      onDocumentClick = (event) => {
        if ((this.hostEl ?? this).contains(event.target)) return;
        this.close();
      };

      onScroll = () => {
        if (!this.isPointer && !this.panel?.hidden) this.close();
      };

      open() {
        if (!this.panel || !this.panel.hidden) return;
        for (const other of document.querySelectorAll('review-popup')) {
          if (other !== this) other.close();
        }
        this.panel.hidden = false;
        this.trigger?.setAttribute('aria-expanded', 'true');
      }

      close() {
        if (!this.panel || this.panel.hidden) return;
        this.panel.hidden = true;
        this.trigger?.setAttribute('aria-expanded', 'false');
      }
    }
  );
}
